import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../Components/Header/Header';
import BrandKitEditor, { suggestLook } from './BrandKitEditor';
import { PlayVoice, VoiceAvatar, VoicePicker, channelVoiceInfo } from './VoiceParts';
import { errorMessage, kitColors, loadSpeechifyVoices, mediaUrl, loadDocChannels, loadDocOptions } from './docShared';
import './documentaries.css';
import './docwizard.css';

function KitRow({ ch, kit, look, active, onPick }) {
  const c = kitColors(look, kit);
  return (
    <button type="button" className={`kits-row ${active ? 'is-on' : ''}`} onClick={onPick} aria-pressed={active}>
      {ch.logo_url
        ? <img className="kits-row__logo" src={mediaUrl(ch.logo_url)} alt="" />
        : <span className="kits-row__logo kits-row__logo--txt">{(ch.name || '?')[0].toUpperCase()}</span>}
      <span className="kits-row__text">
        <strong>{ch.name}</strong>
        <span>{look ? look.title : 'No brand kit yet'}</span>
      </span>
      {look && (
        <span className="bk-swatches" aria-hidden="true">
          {[c.background, c.on_footage, c.accent].map((col, i) => <span key={i} style={{ background: col }} />)}
        </span>
      )}
    </button>
  );
}

export default function BrandKits() {
  const [options, setOptions] = useState(null);
  const [channels, setChannels] = useState(null);
  const [voices, setVoices] = useState(null);
  const [activeId, setActiveId] = useState('');
  const [kits, setKits] = useState({});
  const [picking, setPicking] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([loadDocOptions(), loadDocChannels(true)])
      .then(([o, list]) => {
        setOptions(o);
        setChannels(list);
        const k = {};
        list.forEach((ch) => { k[ch.id] = { ...(ch.brand_kit || {}) }; });
        setKits(k);
        if (list[0]) setActiveId(list[0].id);
      })
      .catch((e) => setError(errorMessage(e, 'Could not load your channels.')));
    loadSpeechifyVoices().then((v) => setVoices(v || []));
  }, []);

  useEffect(() => { setPicking(false); }, [activeId]);

  const ch = channels?.find((c) => c.id === activeId);
  const details = useMemo(() => options?.look_details || {}, [options]);
  const kit = useMemo(() => {
    const k = (ch && kits[ch.id]) || {};
    return ch && !k.look && details ? { ...k, look: suggestLook(ch.niche, details) } : k;
  }, [ch, kits, details]);
  const info = ch ? channelVoiceInfo(ch, kit, voices, options?.voices) : null;
  const setKit = (k) => setKits((p) => ({ ...p, [ch.id]: k }));

  return (
    <div className="docs-page wz-page">
      <Header />
      <main className="wz-main kits-main">
        <div className="wz-head">
          <Link to="/documentaries" className="docs-back">Documentaries</Link>
          <h1>Brand kits</h1>
          <p className="kits-lead">Each channel keeps one brand kit: its look, colours and documentary voice. New
            documentaries for that channel start from it.</p>
        </div>
        {error && <div className="docs-alert docs-alert--bad">{error}</div>}
        {channels && channels.length === 0 && (
          <div className="wz-empty">
            <h3>No channels yet</h3>
            <p>Create a channel, then give it a brand kit here.</p>
            <Link className="docs-btn docs-btn--primary" to="/channels">Go to Channels</Link>
          </div>
        )}
        {channels && channels.length > 0 && (
          <div className="kits">
            <nav className="kits-list" aria-label="Channels">
              {channels.map((c) => (
                <KitRow key={c.id} ch={c} kit={kits[c.id]} look={details[(kits[c.id] || {}).look]}
                        active={c.id === activeId} onPick={() => setActiveId(c.id)} />
              ))}
            </nav>
            {ch && options && (
              <div className="kits-edit" key={ch.id}>
                <section className="wz-voice">
                  <div className="wz-voice__now">
                    <VoiceAvatar voice={info.effectiveObj} name={info.effective?.name} />
                    <div className="wz-voice__text">
                      {info.effective
                        ? <><strong>{info.effective.name}</strong> narrates {ch.name}'s documentaries.</>
                        : <><strong>No documentary voice yet.</strong> {info.own
                          ? `${ch.name} uses ${info.ownName.startsWith('a ') ? info.ownName : `${info.ownName}, which is not a Speechify voice`}. Documentaries need Speechify to time pictures to words.`
                          : 'Choose a Speechify voice.'}</>}
                    </div>
                    {info.effective && <PlayVoice id={`kit-${ch.id}`} voice={info.effectiveObj} label={info.effective.name} />}
                    <div className="wz-voice__actions">
                      {info.override && info.ownUsable && (
                        <button type="button" className="docs-btn docs-btn--ghost"
                                onClick={() => setKit({ ...kit, voice: '', voice_name: '' })}>Use the channel voice</button>
                      )}
                      <button type="button" className="docs-btn" onClick={() => setPicking((p) => !p)}>
                        {picking ? 'Close' : 'Change voice'}
                      </button>
                    </div>
                  </div>
                  {picking && (
                    <VoicePicker voices={voices} selected={info.effective?.id}
                                 onPick={(v) => { setKit({ ...kit, voice: v.id, voice_name: v.display_name }); setPicking(false); }} />
                  )}
                </section>
                <BrandKitEditor options={options} kit={kit} onChange={setKit} channel={ch}
                                onSaved={(u) => setChannels((l) => l.map((c) => (c.id === u.id ? { ...c, brand_kit: u.brand_kit } : c)))} />
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
